// ProtocolConfigContext.tsx
/**
 * Provides the protocol configuration to the ParticipantTracker tree.
 *
 * The server ruleset (GET /api_clinical_trials/get_protocol_config) is the
 * single source of truth; it is fetched once per provider mount and converted
 * via protocolConfigFromRuleset(). There is no static fallback: `config` is
 * null until the fetch resolves, and consumers must gate rendering on it so
 * protocol math never runs against missing config (race protection).
 */

import React, { createContext, useContext, useMemo } from 'react';

import { protocolConfigFromRuleset } from './config/protocolRulesetAdapter';
import { useProtocolConfig } from './hooks/useProtocolConfig';
import { ProtocolConfig } from './types/protocolConfig';

interface ProtocolConfigContextValue {
  /** Server-driven protocol config; null until the ruleset fetch resolves */
  config: ProtocolConfig | null;
  /** True while the server ruleset fetch is in flight */
  isLoading: boolean;
  /** Fetch error — config stays null; surface this instead of rendering */
  error: string | null;
  refetch: () => void;
}

const ProtocolConfigContext = createContext<ProtocolConfigContextValue>({
  config: null,
  isLoading: false,
  error: null,
  refetch: () => undefined,
});

export const ProtocolConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, isLoading, error, refetch } = useProtocolConfig();

  const value = useMemo<ProtocolConfigContextValue>(
    () => ({
      config: data ? protocolConfigFromRuleset(data) : null,
      isLoading,
      error,
      refetch,
    }),
    [data, isLoading, error, refetch]
  );

  return (
    <ProtocolConfigContext.Provider value={value}>
      {children}
    </ProtocolConfigContext.Provider>
  );
};

export const useProtocolConfigContext = (): ProtocolConfigContextValue =>
  useContext(ProtocolConfigContext);
